import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react'
import { Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle, Tabs, TabsContent, TabsList, TabsTrigger } from '@jira-clone/ui'
import { formatDuration, shiftWeek, STATUS_LABEL, weekLabel, weekStartOf } from '../lib/timesheet'
import { getApiErrorMessage } from '../shared/utils/apiError'
import { useSheet, useTimesheetConfig, useTimesheetList, useWeek, type TimesheetSheetDto } from '../features/timesheets/api'
import { ReportsView } from '../features/timesheets/components/ReportsView'
import { SheetPanel } from '../features/timesheets/components/SheetPanel'

const statusVariant = (s: string) => (s === 'approved' ? 'success' : s === 'submitted' ? 'outline' : s === 'rejected' ? 'destructive' : 'secondary')

function MyWeek({ today }: { today: string }) {
  const [weekStart, setWeekStart] = useState(() => weekStartOf(today))
  const week = useWeek(weekStart)
  const currentWeek = weekStartOf(today)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button size="icon" variant="outline" aria-label="Previous week" onClick={() => setWeekStart(shiftWeek(weekStart, -1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-[12rem] text-center">
          <p className="text-sm font-semibold">{weekLabel(weekStart)}</p>
          {weekStart === currentWeek && <p className="text-[11px] text-muted-foreground">This week</p>}
        </div>
        <Button size="icon" variant="outline" aria-label="Next week" disabled={weekStart >= currentWeek} onClick={() => setWeekStart(shiftWeek(weekStart, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        {weekStart !== currentWeek && (
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(currentWeek)}>
            Today
          </Button>
        )}
      </div>

      {week.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {week.error && <p role="alert" className="text-sm text-destructive">{getApiErrorMessage(week.error, 'load this timesheet')}</p>}
      {week.data && <SheetPanel key={week.data.sheet.id} payload={week.data} weekStart={weekStart} />}
    </div>
  )
}

function SheetTable({ sheets, showPerson, onOpen, empty }: { sheets: TimesheetSheetDto[]; showPerson: boolean; onOpen: (s: TimesheetSheetDto) => void; empty: string }) {
  if (sheets.length === 0) return <p className="text-sm text-muted-foreground py-6 text-center">{empty}</p>
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            {showPerson && <th className="px-4 py-2 font-medium">Person</th>}
            <th className="px-4 py-2 font-medium">Week</th>
            <th className="px-4 py-2 font-medium">Logged</th>
            <th className="px-4 py-2 font-medium">Expected</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {sheets.map((s) => (
            <tr key={s.id} className="hover:bg-muted/30">
              {showPerson && (
                <td className="px-4 py-2.5">
                  <p className="font-medium">{s.userName ?? '—'}</p>
                  <p className="text-xs text-muted-foreground">{s.department ?? ''}</p>
                </td>
              )}
              <td className="px-4 py-2.5">{weekLabel(s.weekStartDate, s.weekStart)}</td>
              <td className="px-4 py-2.5 font-mono">{formatDuration(s.totalMinutes)}</td>
              <td className="px-4 py-2.5 font-mono text-muted-foreground">{s.expectedMinutes != null ? formatDuration(s.expectedMinutes) : '—'}</td>
              <td className="px-4 py-2.5">
                <Badge variant={statusVariant(s.status) as any} className="text-[10px] uppercase">
                  {STATUS_LABEL[s.status]}
                </Badge>
              </td>
              <td className="px-4 py-2.5 text-right">
                <Button size="sm" variant="outline" onClick={() => onOpen(s)}>
                  Open
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SheetDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const sheet = useSheet(id)
  return (
    <Dialog open={id !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sheet.data ? `${sheet.data.sheet.userName ?? 'Timesheet'} · ${weekLabel(sheet.data.sheet.weekStartDate, sheet.data.sheet.weekStart)}` : 'Timesheet'}</DialogTitle>
        </DialogHeader>
        {sheet.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {sheet.error && <p role="alert" className="text-sm text-destructive">{getApiErrorMessage(sheet.error, 'load this timesheet')}</p>}
        {sheet.data && <SheetPanel payload={sheet.data} />}
      </DialogContent>
    </Dialog>
  )
}

function History({ onOpen }: { onOpen: (s: TimesheetSheetDto) => void }) {
  const list = useTimesheetList('mine')
  return <SheetTable sheets={list.data?.data ?? []} showPerson={false} onOpen={onOpen} empty={list.isLoading ? 'Loading…' : 'No timesheets yet.'} />
}

function Approvals({ onOpen }: { onOpen: (s: TimesheetSheetDto) => void }) {
  const [status, setStatus] = useState<'submitted' | 'all'>('submitted')
  const list = useTimesheetList('team', status === 'all' ? undefined : status)
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button size="sm" variant={status === 'submitted' ? 'secondary' : 'outline'} onClick={() => setStatus('submitted')}>
          Waiting for approval
        </Button>
        <Button size="sm" variant={status === 'all' ? 'secondary' : 'outline'} onClick={() => setStatus('all')}>
          All
        </Button>
      </div>
      <SheetTable
        sheets={list.data?.data ?? []}
        showPerson
        onOpen={onOpen}
        empty={list.isLoading ? 'Loading…' : status === 'submitted' ? 'Nothing is waiting for you.' : 'No timesheets yet.'}
      />
    </div>
  )
}

function AllTimesheets({ onOpen }: { onOpen: (s: TimesheetSheetDto) => void }) {
  const [status, setStatus] = useState<'all' | 'submitted' | 'approved'>('all')
  const list = useTimesheetList('all', status === 'all' ? undefined : status)
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button size="sm" variant={status === 'all' ? 'secondary' : 'outline'} onClick={() => setStatus('all')}>
          All
        </Button>
        <Button size="sm" variant={status === 'submitted' ? 'secondary' : 'outline'} onClick={() => setStatus('submitted')}>
          Submitted
        </Button>
        <Button size="sm" variant={status === 'approved' ? 'secondary' : 'outline'} onClick={() => setStatus('approved')}>
          Approved
        </Button>
      </div>
      <SheetTable
        sheets={list.data?.data ?? []}
        showPerson
        onOpen={onOpen}
        empty={list.isLoading ? 'Loading…' : 'No timesheets found.'}
      />
    </div>
  )
}

export default function TimesheetsPage() {
  const navigate = useNavigate()
  const config = useTimesheetConfig()
  const [openId, setOpenId] = useState<string | null>(null)
  const canReview = !!config.data?.roles.canReview
  const canViewReports = !!config.data?.roles.canViewReports
  const canViewAll = !!config.data?.roles.canViewAll
  // Shares its cache with the Approvals tab, so the count in the tab label costs nothing extra.
  const waiting = useTimesheetList('team', 'submitted', canReview)
  const waitingCount = canReview ? waiting.data?.total ?? 0 : 0

  if (config.isLoading) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>
  if (config.error || !config.data) {
    return <p role="alert" className="p-6 text-sm text-destructive">{getApiErrorMessage(config.error, 'open timesheets')}</p>
  }

  return (
    <div className="space-y-5 max-w-5xl mx-auto">
      <div className="flex items-center gap-3.5">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Timesheets</h1>
          <p className="text-sm text-muted-foreground">
            Log what you worked on each day and submit your week. Expected: {config.data.workWeek.workDays.length} days of {config.data.workWeek.hoursPerDay}h.
          </p>
        </div>
      </div>

      <Tabs defaultValue="week">
        <TabsList>
          <TabsTrigger value="week">My week</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          {canViewAll && <TabsTrigger value="all">All Timesheets</TabsTrigger>}
          {canReview && <TabsTrigger value="approvals">{waitingCount > 0 ? `Approvals (${waitingCount})` : 'Approvals'}</TabsTrigger>}
          {canViewReports && <TabsTrigger value="reports">Reports</TabsTrigger>}
        </TabsList>
        <TabsContent value="week" className="mt-4">
          <MyWeek today={config.data.today} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <History onOpen={(s) => setOpenId(s.id)} />
        </TabsContent>
        {canViewAll && (
          <TabsContent value="all" className="mt-4">
            <AllTimesheets onOpen={(s) => setOpenId(s.id)} />
          </TabsContent>
        )}
        {canViewReports && (
          <TabsContent value="reports" className="mt-4">
            <ReportsView today={config.data.today} />
          </TabsContent>
        )}
        {canReview && (
          <TabsContent value="approvals" className="mt-4">
            <Approvals onOpen={(s) => setOpenId(s.id)} />
          </TabsContent>
        )}
      </Tabs>

      <SheetDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  )
}
